import { fg } from '@atlaskit/platform-feature-flags';

import type { CurrentPageContextType } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/types';
import { ENTITY_CONTEXT_TYPE, type CurrentEntityContext } from '../../../types';

import type { ContextBannerSmartLinkInfo } from './ContextBannerSmartLinkInfo';

// Transient query params that the Confluence/Jira SPA shell appends mid-navigation
// (e.g. `?atl_f=PAGETREE` when navigating via the page tree). They change the URL
// for a single intermediate render before settling on the clean URL, which would
// otherwise cause the smart card (keyed/resolved by URL) to churn on a junk URL.
const TRANSIENT_URL_PARAMS = ['atl_f'];

/**
 * Strips transient navigation params so the derived URL is stable across a single
 * SPA navigation. Returns the input unchanged if it cannot be parsed.
 */
const normalizeContextUrl = (rawUrl: string): string => {
	if (!rawUrl) {
		return rawUrl;
	}
	try {
		const url = new URL(rawUrl);
		let mutated = false;
		for (const param of TRANSIENT_URL_PARAMS) {
			if (url.searchParams.has(param)) {
				url.searchParams.delete(param);
				mutated = true;
			}
		}
		return mutated ? url.toString() : rawUrl;
	} catch {
		return rawUrl;
	}
};

export const getContextBannerSmartLinkInfo = ({
	currentContext,
	hasBrowserContext,
	browserContextUrl,
	windowHref,
	documentTitle,
}: {
	currentContext: CurrentPageContextType | CurrentEntityContext;
	hasBrowserContext: boolean;
	browserContextUrl: string | undefined;
	windowHref: string;
	documentTitle: string;
}): ContextBannerSmartLinkInfo => {
	if (currentContext.type === ENTITY_CONTEXT_TYPE) {
		return {
			displayUrl: currentContext.url,
			displayTitle: currentContext.name,
			source: 'entity',
		};
	}

	if (
		currentContext.type === 'trello_view_context' &&
		fg('trello_enterprise_rovo_trello_context')
	) {
		return {
			displayUrl: currentContext.payload.url,
			displayTitle: currentContext.payload.name ?? currentContext.payload.url,
			source: 'entity',
		};
	}

	if (hasBrowserContext && browserContextUrl) {
		return {
			displayUrl: browserContextUrl,
			displayTitle: browserContextUrl,
			source: 'browser-context',
		};
	}

	return {
		displayUrl: normalizeContextUrl(windowHref),
		displayTitle: documentTitle,
		source: 'current-page',
	};
};
